// Types
import { RootState } from "@/redux/store";
import { IPageParam } from "@/Common/api/types/pagination";

// Queries
import { getPaginatedNotifications } from "@/Common/api/notifications/queries";

// Helpers
import { buildGetNextPageParam } from "../Pagination/helpers";

// Hooks
import { useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { usePaginatedResource } from "@/Common/hooks/Pagination";

// Constants
import { NOTIFICATIONS_PAGINATION_SIZE, NOTIFICATIONS_STALE_TIME } from "@/Common/hooks/Notifications/constants";

interface IUseNotificationListConfiguration {
	enabled?: boolean;
	showOnlyUnread?: boolean;
	archived?: boolean;
}

export const useNotificationList = ({ enabled = true, showOnlyUnread = true, archived = false }: IUseNotificationListConfiguration) => {
	const userId = useSelector((state: RootState) => state.user.user?._id);
	const locationId = useSelector((state: RootState) => state.location.location?.locationId);
	const queryClient = useQueryClient();

	const { data: notifications, ...otherResults } = usePaginatedResource({
		queryKey: ["notificationList", locationId, userId, showOnlyUnread, archived],
		queryFn: async ({ pageParam }) => {
			if (!userId || !locationId) {
				throw new Error("Cant request notifications without a logged user, or location");
			}

			const typedPageParam = pageParam as IPageParam<string>;
			if (!typedPageParam || !("after" in typedPageParam) || !("first" in typedPageParam)) {
				throw new Error("PageParam should have properties 'after' and 'first'");
			}

			const paginatedResponse = await getPaginatedNotifications({
				locationId,
				onlyUnRead: showOnlyUnread,
				archived,
				...typedPageParam,
			});

			//Update cache with latest pageInfo
			queryClient.setQueryData(["notificationsInfo", userId, locationId], paginatedResponse.pageInfo);

			return paginatedResponse;
		},
		getNextPageParam: buildGetNextPageParam(NOTIFICATIONS_PAGINATION_SIZE),
		enabled: enabled && !!userId && !!locationId,
		initialPageParam: { after: "", first: NOTIFICATIONS_PAGINATION_SIZE },
		staleTime: NOTIFICATIONS_STALE_TIME,
	});

	return {
		notifications,
		...otherResults,
	};
};
